import { z } from 'zod';
import { MonetaryValueBackendRuntype } from '../backendTypes';
import { PackagingRuntype, PriceTypeRuntype } from '../offer';
import { SupplierPreference } from '../supplierAndStockLocation';
import { TotalCostOfOwnershipInputDTORuntype, TotalCostOfOwnershipRuleDTORuntype } from '../totalCostOfOwnershipRules';

export type SourcingTemplateLimit = z.infer<typeof SourcingTemplateLimitRuntype>;
export const SourcingTemplateLimitRuntype = z.object({
    type: z.enum(['Never', 'Days']),
    number: z.number().optional(),
});

export type SourcingTemplateExcessPreference = z.infer<typeof SourcingTemplateExcessPreferenceRuntype>;
export const SourcingTemplateExcessPreferenceRuntype = z.object({
    max_ratio_percent: z.number().nullable(),
    minimum_savings: MonetaryValueBackendRuntype.nullable(),
});

export type SourcingTemplateOfferAgeLimit = z.infer<typeof SourcingTemplateOfferAgeLimitRuntype>;
export const SourcingTemplateOfferAgeLimitRuntype = z.object({
    expiring_before_validity_date: SourcingTemplateLimitRuntype,
    expired_after_validity_date: SourcingTemplateLimitRuntype,
    old_after_update: SourcingTemplateLimitRuntype,
    outdated_after_update: SourcingTemplateLimitRuntype,
});

export enum SourcingTemplateSourcingPreferenceOptions {
    Fastest = 'Fastest',
    BestPrice = 'BestPrice',
    BestPriceBy = 'BestPriceBy',
}
export const SourcingTemplateSourcingPreferenceOptionsRuntype = z.nativeEnum(SourcingTemplateSourcingPreferenceOptions);

export type SourcingTemplateLeadTimePreferences = z.infer<typeof SourcingTemplateLeadTimePreferencesRuntype>;
export const SourcingTemplateLeadTimePreferencesRuntype = z.object({
    type: SourcingTemplateSourcingPreferenceOptionsRuntype,
    target: z.string().optional(),
});

export enum SourcingTemplateInventoryPreference {
    PreferInventory = 'PreferInventory',
    Neutral = 'Neutral',
    PreferMarketOffers = 'PreferMarketOffers',
}
export const SourcingTemplateInventoryPreferenceRuntype = z.nativeEnum(SourcingTemplateInventoryPreference);

export enum SolutionComparisonCriteria {
    UnitPrice = 'UnitPrice',
    TotalPrice = 'TotalPrice',
}

export type SourcingTemplateSolutionPreferenceDTO = z.infer<typeof SourcingTemplateSolutionPreferenceDTORuntype>;
export const SourcingTemplateSolutionPreferenceDTORuntype = z.object({
    lead_time_preferences: SourcingTemplateLeadTimePreferencesRuntype,
    solution_comparison_criteria: z.nativeEnum(SolutionComparisonCriteria),
    offer_age_limit: SourcingTemplateOfferAgeLimitRuntype,
    inventory_preference: SourcingTemplateInventoryPreferenceRuntype,
    packaging_preferences: z.array(PackagingRuntype),
    prefer_direct_api_integrations: z.boolean(),
    excluded_price_types: z.array(PriceTypeRuntype),
    exclude_mismatched_price_points: z.boolean(),
    prioritize_preferred_manufacturers: z.boolean(),
    prefer_customer_negotiated_prices: z.boolean(),
    is_tco_enabled: z.boolean(),
    excess_preference: z.unknown(),
    low_stock_warning_factor: z.unknown(),
});

export enum SourcingTemplateTags {
    Sourcing = 'Sourcing',
}
export const SourcingTemplateTagsRuntype = z.nativeEnum(SourcingTemplateTags);

export type SourcingTemplateDTO = z.infer<typeof SourcingTemplateDTORuntype>;
export const SourcingTemplateDTORuntype = z.object({
    id: z.string().uuid(),
    name: z.string(),
    description: z.string().nullable(),
    is_default: z.boolean(),
    solution_preference: SourcingTemplateSolutionPreferenceDTORuntype,
    supplier_and_stock_location_preferences: z.array(
        z.object({
            supplier_and_stock_location_id: z.string().uuid(),
            kind: z.nativeEnum(SupplierPreference),
        }),
    ),
    inventory_site_preferences: z.array(
        z.object({
            inventory_site_id: z.string().uuid(),
            kind: z.nativeEnum(SupplierPreference),
        }),
    ),
    total_cost_of_ownership_rules: TotalCostOfOwnershipRuleDTORuntype.array(),
    created_at: z.string().datetime(),
    updated_at: z.string().datetime(),
});

export type SolutionPreferencesInputDTO = z.infer<typeof SolutionPreferencesInputDTORuntype>;
export const SolutionPreferencesInputDTORuntype = z.object({
    lead_time_preferences: SourcingTemplateLeadTimePreferencesRuntype,
    solution_comparison_criteria: z.nativeEnum(SolutionComparisonCriteria),
    offer_age_limit: SourcingTemplateOfferAgeLimitRuntype,
    inventory_preference: SourcingTemplateInventoryPreferenceRuntype,
    packaging_preferences: z.array(PackagingRuntype),
    prefer_direct_api_integrations: z.boolean(),
    excluded_price_types: z.array(PriceTypeRuntype),
    exclude_mismatched_price_points: z.boolean(),
    prioritize_preferred_manufacturers: z.boolean(),
    prefer_customer_negotiated_prices: z.boolean(),
    is_tco_enabled: z.boolean(),
});

export type SourcingTemplateInputDTO = z.infer<typeof SourcingTemplateInputDTORuntype>;
export const SourcingTemplateInputDTORuntype = z.object({
    name: z.string(),
    description: z.string().nullable(),
    is_default: z.boolean(),
    solution_preference: SolutionPreferencesInputDTORuntype,
    supplier_and_stock_location_preferences: z.array(
        z.object({
            supplier_and_stock_location_id: z.string().uuid(),
            kind: z.nativeEnum(SupplierPreference),
        }),
    ),
    inventory_site_preferences: z.array(
        z.object({
            inventory_site_id: z.string().uuid(),
            kind: z.nativeEnum(SupplierPreference),
        }),
    ),
    total_cost_of_ownership_rules: TotalCostOfOwnershipInputDTORuntype.array(),
    origin: z.string().uuid().nullable(),
});
